import { useMemo } from "react";
import { useUnreads } from "@talkjs/react";

/**
 * Set of TalkJS conversation IDs (from `allowedConversationIds`) that
 * currently have unread messages. The presence-only counterpart of
 * `useChatUnreadCount`: same filtering rule (intersect with the
 * allowed-id set so callers don't get unread state for conversations
 * outside the active program), but instead of summing counts it
 * surfaces *which* conversations have unread, so per-conversation
 * presence indicators (e.g. the dropdown row dots in
 * `ChatCircleSwitcher`) can render without each one calling
 * `useUnreads` independently.
 *
 * Returns an empty Set when `allowedConversationIds` is null/empty or
 * `useUnreads` hasn't yielded data yet.
 */
export function useChatUnreadConversationIds(
  allowedConversationIds: readonly string[] | null | undefined
): Set<string> {
  const unreads = useUnreads();

  return useMemo(() => {
    if (!allowedConversationIds || allowedConversationIds.length === 0) {
      return new Set<string>();
    }
    if (!unreads || unreads.length === 0) {
      return new Set<string>();
    }
    const allowed = new Set(allowedConversationIds);
    const result = new Set<string>();
    for (const item of unreads) {
      if (
        (item.unreadMessageCount ?? 0) > 0 &&
        allowed.has(item.conversation.id)
      ) {
        result.add(item.conversation.id);
      }
    }
    return result;
  }, [allowedConversationIds, unreads]);
}

export default useChatUnreadConversationIds;
